import { Injectable } from '@angular/core';
import { take } from 'rxjs/operators';
import { combineLatest } from 'rxjs';
import * as ExcelJS from 'exceljs';
import { StatoSessioneService } from '../../../../shared/services/stato-sessione.service';
import {
  Ambiguity,
  AnalysisContent,
  DataParametersAnalysis, DataParametersResponse, DynamicRefinedTestCase, StoryDescriptionResponse,
  SupportiveAnalysisContent, TokenCount
} from '../../../../shared/templateGen/templateInterfaces';
import { TestWithMetadata } from '../test-id.service';
import { StripHtmlPipe } from '../../../../shared/pipes/strip-html.pipe';
import { MacroScenarioItem, MacroScenarioMicroSpecState } from '../../template/three-column-workspace/three-column-workspace.component';


interface SavedAppState {
  version: string;
  timestamp: string;
  metadata: {
    projectId: string;
    author: string;
    selectedModel: string;
    projectLanguage: string;
    templateName: string;
    totalTests: number;
    tokenUsage?: TokenCount;
  };
  criteriaGroups: any[];
  generatedTests: any[];
  selectedTemplate: string;
  selectedTemplateColumns: string[];
  context: string;
  istruzioniScriviTest: string;
  // Additional state fields with precise types
  ambiguities?: Ambiguity[];
  coreAnalysis?: AnalysisContent | null;
  requirementsAnalysis?: AnalysisContent | null;
  supportiveAnalysis?: SupportiveAnalysisContent | null;
  macroScenarios?: MacroScenarioItem[];
  dataParameters?: {
    analysis_results: DataParametersAnalysis;
  } | null;
  storyDescription?: {
    story_id: string;
    title: string;
    description: string;
  } | null;
  // New comprehensive state fields
  microScenarioSpecifications?: { [key: string]: MacroScenarioMicroSpecState };
  detailedBreakdowns?: { [key: string]: string };
  refinedTestCases?: { [key: string]: DynamicRefinedTestCase[] };
  globalConsolidatedTests?: DynamicRefinedTestCase[];
}

@Injectable({
  providedIn: 'root'
})
export class DownloaderComponent {
  private readonly STATE_VERSION = '1.1.1'; // Increment version to reflect enhanced state (added requirementsAnalysis)
  private memoizedSubTableRows: Map<string, any[]> = new Map<string, any[]>();
  constructor(private sessioneService: StatoSessioneService) { }

  saveCurrentState(author: string, filename: string): void {
    // Combine all necessary observables to get the current state
    combineLatest([
      this.sessioneService.generatedTests$,
      this.sessioneService.criteriaGroups$,
      this.sessioneService.selectedTemplate$,
      this.sessioneService.templates$,
      this.sessioneService.projectId$,
      this.sessioneService.selectedModel$,
      this.sessioneService.projectLanguage$,
      this.sessioneService.istruzioniScriviTest$,
      this.sessioneService.refinedStoryString$,
      this.sessioneService.ambiguities$,
      this.sessioneService.coreAnalysis$,
      this.sessioneService.requirementsAnalysis$,
      this.sessioneService.supportiveAnalysis$,
      this.sessioneService.macroScenarios$,
      this.sessioneService.dataParameters$,
      this.sessioneService.storyDescription$,
      // Add all new observables for a complete session snapshot
      this.sessioneService.isAgenticMode$,
      this.sessioneService.tokenUsage$,
      this.sessioneService.microScenarioSpecifications$,
      this.sessioneService.detailedBreakdowns$,
      this.sessioneService.refinedTestCases$,
      this.sessioneService.globalConsolidatedTests$
    ]).pipe(
      take(1)
    ).subscribe(([
      tests,
      criteriaGroups,
      selectedTemplate,
      templates,
      projectId,
      selectedModel,
      projectLanguage,
      istruzioniScriviTest,
      context,
      ambiguities,
      coreAnalysis,
      requirementsAnalysis,
      supportiveAnalysis,
      macroScenarios,
      dataParameters,
      storyDescription,
      // New variables from observables
      agenticMode,
      tokenUsage,
      microScenarioSpecifications,
      detailedBreakdowns,
      refinedTestCases,
      globalConsolidatedTests
    ]) => {
      // Cast to the correct type
      const generatedTests = tests as TestWithMetadata[];

      // Sanitize existing fields
      const sanitizedGeneratedTests = Array.isArray(generatedTests) ? generatedTests.map(test => ({ ...test, data: { ...test.data, ID: test.metadata.sequentialId || 0 } })) : [];
      const sanitizedCriteriaGroups = Array.isArray(criteriaGroups) ? criteriaGroups : [];
      const sanitizedSelectedTemplate = typeof selectedTemplate === 'string' ? selectedTemplate : '';
      const sanitizedSelectedTemplateColumns = Array.isArray(templates) ? (templates.find(t => t.templateName === selectedTemplate)?.columns || []) : [];
      const sanitizedProjectId = typeof projectId === 'string' ? projectId : '';
      const sanitizedSelectedModel = typeof selectedModel === 'string' ? selectedModel : '';
      const sanitizedProjectLanguage = typeof projectLanguage === 'string' ? projectLanguage : '';
      const sanitizedIstruzioniScriviTest = typeof istruzioniScriviTest === 'string' ? istruzioniScriviTest : '';
      const sanitizedContext = typeof context === 'string' ? context : '';
      const sanitizedAmbiguities = Array.isArray(ambiguities) ? ambiguities : [];
      const sanitizedMacroScenarios = Array.isArray(macroScenarios) ? macroScenarios : [];
      const sanitizedDataParameters = this.processDataParameters(dataParameters);
      const sanitizedStoryDescription = this.processStoryDescription(storyDescription);

      // Sanitize new fields
      const sanitizedAgenticMode = typeof agenticMode === 'boolean' ? agenticMode : false;
      const sanitizedTokenUsage = (tokenUsage && typeof tokenUsage === 'object' && 'total_tokens' in tokenUsage) ? tokenUsage as TokenCount : undefined;
      const microSpecsObject = microScenarioSpecifications instanceof Map ? Object.fromEntries(microScenarioSpecifications) : undefined;
      const breakdownsObject = detailedBreakdowns instanceof Map ? Object.fromEntries(detailedBreakdowns) : undefined;
      const refinedTestCasesObject = refinedTestCases instanceof Map ? Object.fromEntries(refinedTestCases) : undefined;
      const sanitizedGlobalConsolidatedTests = Array.isArray(globalConsolidatedTests) ? globalConsolidatedTests : undefined;

      const state: SavedAppState = {
        version: this.STATE_VERSION,
        timestamp: new Date().toISOString(),
        metadata: {
          projectId: sanitizedProjectId,
          author,
          selectedModel: sanitizedSelectedModel,
          projectLanguage: sanitizedProjectLanguage,
          templateName: sanitizedSelectedTemplate,
          totalTests: sanitizedGeneratedTests.length,
          tokenUsage: sanitizedTokenUsage,
        },
        criteriaGroups: sanitizedCriteriaGroups,
        generatedTests: sanitizedGeneratedTests,
        selectedTemplate: sanitizedSelectedTemplate,
        selectedTemplateColumns: sanitizedSelectedTemplateColumns,
        context: sanitizedContext,
        istruzioniScriviTest: sanitizedIstruzioniScriviTest,
        ambiguities: sanitizedAmbiguities,
        coreAnalysis: this.processAnalysisContent(coreAnalysis),
        requirementsAnalysis: this.processAnalysisContent(requirementsAnalysis),
        supportiveAnalysis: this.processSupportiveAnalysisContent(supportiveAnalysis),
        macroScenarios: sanitizedMacroScenarios,
        dataParameters: sanitizedDataParameters,
        storyDescription: sanitizedStoryDescription,
        // Add new state fields
        microScenarioSpecifications: microSpecsObject,
        detailedBreakdowns: breakdownsObject,
        refinedTestCases: refinedTestCasesObject,
        globalConsolidatedTests: sanitizedGlobalConsolidatedTests,
      };

      this.generateHTMLFile(state, filename);
    });
  }

  /**
   * Safely processes dataParameters to extract analysis_results without token_usage
   */
  private processDataParameters(dataParameters: any): {
    analysis_results: DataParametersAnalysis;
  } | null {
    if (!dataParameters) return null;

    // Check if it's a DataParametersResponse object with analysis_results
    if (dataParameters &&
      typeof dataParameters === 'object' &&
      'analysis_results' in dataParameters) {
      return {
        analysis_results: dataParameters.analysis_results
      };
    }
    return null;
  }

  /**
   * Safely processes storyDescription to extract needed fields without token_usage
   */
  private processStoryDescription(storyDescription: any): {
    story_id: string;
    title: string;
    description: string;
  } | null {
    if (!storyDescription) return null;

    // Check if it's a StoryDescriptionResponse object with required fields
    if (storyDescription &&
      typeof storyDescription === 'object' &&
      'story_id' in storyDescription &&
      'title' in storyDescription &&
      'description' in storyDescription) {
      return {
        story_id: storyDescription.story_id,
        title: storyDescription.title,
        description: storyDescription.description
      };
    }
    return null;
  }

  /**
   * Safely processes AnalysisContent to ensure it has the correct structure
   */
  private processAnalysisContent(analysis: any): AnalysisContent | null {
    if (!analysis) return null;

    // Check if it's a proper AnalysisContent object
    if (analysis &&
      typeof analysis === 'object' &&
      'scenario_analysis' in analysis &&
      'config_testdata_analysis' in analysis) {
      return {
        scenario_analysis: analysis.scenario_analysis,
        config_testdata_analysis: analysis.config_testdata_analysis
      };
    }

    // It might be inside an AnalysisResult object
    if (analysis &&
      typeof analysis === 'object' &&
      'core_analysis' in analysis &&
      typeof analysis.core_analysis === 'object') {
      return this.processAnalysisContent(analysis.core_analysis);
    }

    return null;
  }

  /**
   * Safely processes SupportiveAnalysisContent to ensure it has the correct structure
   */
  private processSupportiveAnalysisContent(analysis: any): SupportiveAnalysisContent | null {
    if (!analysis) return null;

    // Check if it's a proper SupportiveAnalysisContent object
    if (analysis &&
      typeof analysis === 'object' &&
      'scenario_enrichments' in analysis &&
      'config_testdata_enrichments' in analysis) {
      return {
        scenario_enrichments: analysis.scenario_enrichments,
        config_testdata_enrichments: analysis.config_testdata_enrichments
      };
    }

    // It might be inside an AnalysisResult object
    if (analysis &&
      typeof analysis === 'object' &&
      'supportive_analysis' in analysis) {
      return this.processSupportiveAnalysisContent(analysis.supportive_analysis);
    }

    return null;
  }

  private generateHTMLFile(state: SavedAppState, filename: string): void {
    // --- Helper function to generate Ambiguities HTML ---
    const generateAmbiguitiesHtml = (): string => {
      if (!state.ambiguities || state.ambiguities.length === 0) return '';
      return `
        <div class="data-section-container">
          <h2>Ambiguities and Clarifications</h2>
          ${state.ambiguities.map(amb => `
            <div class="data-item">
              <div class="data-label">Question:</div>
              <p>${this.escapeHtml(amb.clarification_needed)}</p>
              <div class="data-label">Answer:</div>
              <p>${this.escapeHtml(amb.resolution || 'N/A')}</p>
            </div>
          `).join('')}
        </div>
      `;
    };

    // --- Helper function to generate Requirements HTML ---
    const generateRequirementsHtml = (): string => {
      if (!state.requirementsAnalysis?.scenario_analysis) return '';
      const reqList = state.requirementsAnalysis.scenario_analysis.split('\n').filter(r => r.trim() !== '');
      return `
        <div class="data-section-container">
            <h2>Identified Requirements</h2>
            <ul class="requirements-list">
                ${reqList.map(req => `<li>${this.escapeHtml(req)}</li>`).join('')}
            </ul>
        </div>
      `;
    };

    // --- Helper function to generate Macro Scenarios HTML ---
    const generateMacroScenariosHtml = (): string => {
      if (!state.macroScenarios || state.macroScenarios.length === 0) return '';
      return `
        <div class="data-section-container">
            <h2>Macro Scenarios</h2>
            ${state.macroScenarios.map(macro => `
                <div class="data-item macro-scenario">
                    <h3>${this.escapeHtml(macro.id)}. ${this.escapeHtml(macro.scenario_title)}</h3>
                    <p><strong>Narrative:</strong> ${this.escapeHtml(macro.narrative_description)}</p>
                    <div class="gwt-steps">
                        <strong>GIVEN:</strong> <ul>${macro.steps.given.map(s => `<li>${this.escapeHtml(s)}</li>`).join('')}</ul>
                        <strong>WHEN:</strong> <ul>${macro.steps.when.map(s => `<li>${this.escapeHtml(s)}</li>`).join('')}</ul>
                        <strong>THEN:</strong> <ul>${macro.steps.then.map(s => `<li>${this.escapeHtml(s)}</li>`).join('')}</ul>
                    </div>
                </div>
            `).join('')}
        </div>
      `;
    };

    // --- Helper function to generate Micro Scenarios HTML ---
    const generateMicroScenariosHtml = (): string => {
      if (!state.microScenarioSpecifications || Object.keys(state.microScenarioSpecifications).length === 0) return '';
      return `
        <div class="data-section-container">
            <h2>Micro Scenario Specifications</h2>
            ${Object.entries(state.microScenarioSpecifications).map(([macroId, specData]) => `
                <div class="micro-group">
                    <h4>From Macro Scenario #${macroId}</h4>
                    ${specData.specifications.map(spec => `
                        <div class="data-item micro-scenario">
                            <strong>${this.escapeHtml(spec.id)}: ${this.escapeHtml(spec.title)}</strong>
                            <p>${this.escapeHtml(spec.specification)}</p>
                        </div>
                    `).join('')}
                </div>
            `).join('')}
        </div>
      `;
    };

    // --- Helper function to generate Consolidated Tests HTML Table ---
    const generateConsolidatedTestsHtml = (): string => {
      if (!state.globalConsolidatedTests || state.globalConsolidatedTests.length === 0) return '';
      const headers = ['ID', 'Title', 'Rationale', 'GIVEN', 'WHEN', 'THEN', 'Source Tests'];
      const rows = state.globalConsolidatedTests.map(test => `
        <tr>
          <td>${this.formatTableCell(test.data.ID)}</td>
          <td>${this.formatTableCell(test.data.TITLE)}</td>
          <td>${this.formatTableCell(test.rationale)}</td>
          <td>${this.formatTableCell(test.data.GIVEN)}</td>
          <td>${this.formatTableCell(test.data.WHEN)}</td>
          <td>${this.formatTableCell(test.data.THEN)}</td>
          <td>${this.formatTableCell(test.sourceTestIds)}</td>
        </tr>
      `).join('');

      return `
        <div class="data-section-container">
            <h2>Consolidated Test Cases (${state.globalConsolidatedTests.length})</h2>
            <table>
                <thead><tr>${headers.map(h => `<th>${this.escapeHtml(h)}</th>`).join('')}</tr></thead>
                <tbody>${rows}</tbody>
            </table>
        </div>
      `;
    };

    // --- Helper function to generate Raw Tests HTML Table ---
    const generateRawTestsHtml = (): string => {
      if (!state.generatedTests || state.generatedTests.length === 0) return '';
      const headers = new Set<string>();
      state.generatedTests.forEach(test => Object.keys(test.data || {}).forEach(key => headers.add(key)));
      const tableHeaders = Array.from(headers);
      const tableRows = state.generatedTests.map(test => `
        <tr>
          ${tableHeaders.map(header => `<td>${this.formatTableCell(test.data[header])}</td>`).join('')}
        </tr>
      `).join('');

      return `
        <div class="data-section-container">
            <h2>Raw Generated Tests (${state.generatedTests.length})</h2>
            <table>
                <thead><tr>${tableHeaders.map(h => `<th>${this.escapeHtml(h)}</th>`).join('')}</tr></thead>
                <tbody>${tableRows}</tbody>
            </table>
        </div>
      `;
    };

    // --- Main HTML Content Assembly ---
    const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Session Report: ${this.escapeHtml(filename)}</title>
    <script type="application/json" id="app-state">
        ${JSON.stringify(state, null, 2).replace(/<\/script>/g, '<\\/script>')}
    </script>
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 1200px; margin: 0 auto; padding: 20px; background-color: #f4f4f4; }
        .metadata-container, .data-section-container, #restore-instructions { background-color: #fff; padding: 20px; border-radius: 8px; margin-bottom: 20px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
        .metadata-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 20px; }
        .metadata-item { padding: 10px; background-color: #f8f9fa; border-radius: 4px; }
        .metadata-label { font-weight: bold; color: #2c3e50; margin-bottom: 5px; }
        .state-info { margin-top: 20px; padding: 10px; background-color: #e9ecef; border-radius: 4px; }
        h2 { border-bottom: 2px solid #3498db; padding-bottom: 10px; margin-top: 0; }
        h3 { color: #2980b9; }
        h4 { color: #555; margin-top: 20px; border-bottom: 1px solid #ccc; padding-bottom: 5px; }
        table { border-collapse: collapse; width: 100%; margin-bottom: 20px; }
        th, td { border: 1px solid #ddd; padding: 12px; text-align: left; vertical-align: top; }
        th { background-color: #3498db; color: white; font-weight: bold; }
        tr:nth-child(even) { background-color: #f2f2f2; }
        ul { margin: 5px 0; padding-left: 20px; }
        .data-item { background-color: #fdfdfd; border: 1px solid #eaeaea; border-left: 5px solid #3498db; padding: 15px; margin-bottom: 15px; border-radius: 5px; }
        .data-item .data-label { font-weight: bold; color: #333; }
        .gwt-steps ul { margin-bottom: 10px; }
        .gwt-steps strong { display: block; margin-top: 5px; color: #2c3e50; }
        .requirements-list { list-style-type: decimal; }
        .micro-group { margin-bottom: 25px; }
    </style>
</head>
<body>
    <div class="metadata-container">
        <h2>Session Information</h2>
        <div class="metadata-grid">
            <div class="metadata-item"><div class="metadata-label">Project ID</div><div>${state.metadata.projectId}</div></div>
            <div class="metadata-item"><div class="metadata-label">Author</div><div>${state.metadata.author}</div></div>
            <div class="metadata-item"><div class="metadata-label">Model</div><div>${state.metadata.selectedModel}</div></div>
            <div class="metadata-item"><div class="metadata-label">Language</div><div>${state.metadata.projectLanguage}</div></div>
            <div class="metadata-item"><div class="metadata-label">Template</div><div>${state.metadata.templateName}</div></div>
            <div class="metadata-item"><div class="metadata-label">Total Raw Tests</div><div>${state.generatedTests?.length || 0}</div></div>
        </div>
        <div class="state-info">
            <p><strong>State Version:</strong> ${state.version}</p>
            <p><strong>Saved:</strong> ${new Date(state.timestamp).toLocaleString()}</p>
        </div>
    </div>

    ${generateAmbiguitiesHtml()}
    ${generateRequirementsHtml()}
    ${generateMacroScenariosHtml()}
    ${generateMicroScenariosHtml()}
    ${generateConsolidatedTestsHtml()}
    ${generateRawTestsHtml()}

    <div id="restore-instructions">
        <h3>How to Restore This Session</h3>
        <p>This HTML file contains all the data from your session embedded within it. To restore your work:</p>
        <ol>
            <li>Upload this file back to the application using the "Upload Workspace" button.</li>
            <li>The application will automatically read the data and restore your entire session.</li>
        </ol>
    </div>
</body>
</html>`;

    // Create and download the file
    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private formatTableCell(value: any): string {
    if (value === null || value === undefined) {
      return '—';
    }

    if (Array.isArray(value)) {
      return this.escapeHtml(value.join('; '));
    }

    if (typeof value === 'object') {
      try {
        return this.escapeHtml(JSON.stringify(value));
      } catch (e) {
        return '[Complex Object]';
      }
    }

    return this.escapeHtml(String(value));
  }

  private escapeHtml(unsafe: string): string {
    return unsafe
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  async loadSavedState(file: File): Promise<SavedAppState | null> {
    try {
      const content = await this.readFileContent(file);
      const parser = new DOMParser();
      const doc = parser.parseFromString(content, 'text/html');
      const stateScript = doc.getElementById('app-state');

      if (!stateScript) {
        throw new Error('No state data found in file');
      }

      const state: SavedAppState = JSON.parse(stateScript.textContent || '');

      // Validate state version and structure
      if (!this.isValidStateVersion(state.version)) {
        throw new Error('Incompatible state version');
      }

      return state;
    } catch (error) {
      console.error('Error loading saved state:', error);
      return null;
    }
  }

  private isValidStateVersion(version: string): boolean {
    // Add version compatibility logic here
    const currentVersion = this.STATE_VERSION.split('.');
    const fileVersion = version.split('.');

    // Major version must match
    return currentVersion[0] === fileVersion[0];
  }

  private readFileContent(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  }

  /**
   * Restores application state from a saved state object.
   * Now includes logic for all new comprehensive state fields.
   */
  async restoreState(state: SavedAppState, includeTests: boolean = true, updateTemplate: boolean = true): Promise<boolean> {
    try {
      // Restore metadata properties
      if (state.metadata) {
        // TODO: aggiungere logica prendere il modello che era selezionato
        //this.sessioneService.updateSelectedModel(state.metadata.selectedModel);
        this.sessioneService.updateProjectLanguage(state.metadata.projectLanguage);
        if (state.metadata.tokenUsage) {
          // Assumes setTokenUsage exists based on SessionPersistenceService
          this.sessioneService.setTokenUsage(state.metadata.tokenUsage);
        }
      }

      // Restore template, context, and instructions
      if (updateTemplate && state.selectedTemplate) {
        this.sessioneService.updateSelectedTemplate(state.selectedTemplate);
      }
      this.sessioneService.updateCriteriaGroups(state.criteriaGroups);
      this.sessioneService.updateRefinedStoryString(state.context);
      this.sessioneService.updateIstruzioniScriviTest(state.istruzioniScriviTest);

      // Restore analysis and descriptive components
      if (state.ambiguities) this.sessioneService.updateAmbiguities(state.ambiguities);
      this.sessioneService.updateAnalysisData(state.coreAnalysis || null, state.requirementsAnalysis || null, state.supportiveAnalysis || null);
      if (state.macroScenarios) this.sessioneService.updateMacroScenarios(state.macroScenarios);

      if (state.dataParameters) {
        // Reconstruct the full object for service compatibility
        const fullDataParameters: DataParametersResponse = {
          analysis_results: state.dataParameters.analysis_results,
          token_usage: { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 }
        };
        this.sessioneService.updateDataParameters(fullDataParameters);
      }

      if (state.storyDescription) {
        // Reconstruct the full object for service compatibility
        const fullStoryDescription: StoryDescriptionResponse = {
          story_id: state.storyDescription.story_id,
          title: state.storyDescription.title,
          description: state.storyDescription.description,
          token_usage: { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 }
        };
        this.sessioneService.updateStoryDescription(fullStoryDescription);
      }

      // Restore NEW comprehensive state fields
      if (state.microScenarioSpecifications) {
        // Convert the saved object back into a Map
        const microSpecMap = new Map(Object.entries(state.microScenarioSpecifications));
        this.sessioneService.updateMicroScenarioSpecifications(microSpecMap);
      }
      if (state.detailedBreakdowns) {
        // Convert the saved object back into a Map
        const breakdownsMap = new Map(Object.entries(state.detailedBreakdowns));
        this.sessioneService.updateDetailedBreakdowns(breakdownsMap);
      }
      if (state.refinedTestCases) {
        // Convert the saved object back into a Map
        const refinedTestCasesMap = new Map(Object.entries(state.refinedTestCases));
        this.sessioneService.setAllRefinedTestCasesMap(refinedTestCasesMap);
      }
      if (state.globalConsolidatedTests) {
        this.sessioneService.updateGlobalConsolidatedTests(state.globalConsolidatedTests);
      }

      // Restore generated tests
      if (includeTests && Array.isArray(state.generatedTests) && state.generatedTests.length > 0) {
        this.sessioneService.updateGeneratedTests(state.generatedTests);
      } else {
        this.sessioneService.updateGeneratedTests([]);
      }

      console.log('State restored successfully', {
        testsRestored: includeTests ? state.generatedTests.length : 0,
        testsSkipped: !includeTests,
        hasMicroSpecs: !!state.microScenarioSpecifications,
        hasBreakdowns: !!state.detailedBreakdowns,
        hasRefinedTests: !!state.refinedTestCases,
        hasGlobalTests: !!state.globalConsolidatedTests,
      });

      return true;
    } catch (error) {
      console.error('Error restoring state:', error);
      return false;
    }
  }

  isSubTable(value: any): boolean {
    const result = value &&
      typeof value === 'object' &&
      (
        (value.headers && Array.isArray(value.headers) && value.rows && Array.isArray(value.rows)) ||
        (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && !Array.isArray(value[0]))
      );

    return result;
  }

  private getSubTableRowCount(subTableData: any): number {
    if (Array.isArray(subTableData)) {
      return subTableData.length;
    }
    return subTableData?.rows?.length || 0;
  }

  private sanitizeCSVValue(value: any, delimiter: string): string {
    if (value === null || value === undefined) {
      return '';
    }

    // Strip HTML tags before CSV formatting
    let stringValue = StripHtmlPipe.strip(String(value));

    // Collapse newlines to '; ' so cell values stay on a single CSV row
    stringValue = stringValue.replace(/\n+/g, '; ').trim();

    // Check if value contains delimiter or quotes
    if (stringValue.includes(delimiter) ||
      stringValue.includes('"')) {
      // Escape quotes and wrap in quotes
      return `"${stringValue.replace(/"/g, '""')}"`;
    }

    return stringValue;
  }

  exportTestsAsCSVWithColumns(filename: string, selectedColumns: string[], delimiter: string = '|'): void {
    this.sessioneService.generatedTests$
      .pipe(take(1))
      .subscribe((generatedTests) => {
        // Call the new generic method
        this.exportToCSVFromData(generatedTests, filename, selectedColumns, delimiter);
      });
  }

  exportTestsAsExcelWithSubtables(
    filename: string,
    selectedColumns: string[] = [],
    options: {
      sheetName?: string;
      addFilters?: boolean;
      freezeHeader?: boolean;
    } = {}
  ): void {
    this.sessioneService.generatedTests$
      .pipe(take(1))
      .subscribe((generatedTests) => {
        // Call the new generic method
        this.exportToExcelFromData(generatedTests, filename, selectedColumns, options);
      });
  }

  // NEW method for CSV export from provided data
  public exportToCSVFromData(
    tests: TestWithMetadata[],
    filename: string,
    selectedColumns: string[],
    delimiter: string = '|'
  ): void {
    if (!tests || tests.length === 0) {
      console.warn('No tests provided to exportToCSVFromData');
      // Optionally show a toastr message here if needed
      return;
    }

    let csvContent = '\ufeff'; // BOM for Excel UTF-8 compatibility
    csvContent += selectedColumns.join(delimiter) + '\n';

    tests.forEach(test => {
      const rowValues = selectedColumns.map(column => {
        let cellValue;
        if (column === 'ID') {
          cellValue = test.metadata.sequentialId !== undefined ? test.metadata.sequentialId : (test.metadata.testId || '');
        } else {
          cellValue = test.data ? test.data[column] : '';
        }

        if (this.isSubTable(cellValue)) {
          return `SubTable(${this.getSubTableRowCount(cellValue)} rows)`;
        } else if (Array.isArray(cellValue)) {
          return cellValue.map(item => this.sanitizeCSVValue(item, delimiter)).join(';');
        } else {
          return this.sanitizeCSVValue(cellValue, delimiter);
        }
      });
      csvContent += rowValues.join(delimiter) + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // NEW method for Excel export from provided data
  public async exportToExcelFromData(
    tests: TestWithMetadata[],
    filename: string,
    selectedColumns: string[] = [],
    options: {
      sheetName?: string;
      addFilters?: boolean;
      freezeHeader?: boolean;
    } = {}
  ): Promise<void> {
    if (!tests || tests.length === 0) {
      console.warn('No tests provided to exportToExcelFromData');
      return;
    }

    const excelOptions = {
      sheetName: options.sheetName || 'Tests',
      addFilters: options.addFilters !== undefined ? options.addFilters : true,
      freezeHeader: options.freezeHeader !== undefined ? options.freezeHeader : true
    };

    const allAvailableColumnsFromData = new Set<string>();
    tests.forEach(test => {
      if (test.data) {
        Object.keys(test.data).forEach(key => {
          if (key !== 'metadata') {
            allAvailableColumnsFromData.add(key);
          }
        });
      }
    });
    allAvailableColumnsFromData.add('ID');

    const columnsForExport = selectedColumns.length > 0
      ? selectedColumns.filter(col => allAvailableColumnsFromData.has(col))
      : Array.from(allAvailableColumnsFromData);

    if (columnsForExport.length === 0) {
      console.warn('No valid columns selected or derived for Excel export.');
      return;
    }

    const subtableColumnsKeys: string[] = []; // Store original keys of subtable columns
    columnsForExport.forEach(colKey => {
      if (colKey === 'ID') return;
      for (const test of tests) {
        if (test.data && test.data[colKey] !== null && test.data[colKey] !== undefined) {
          if (this.isSubTable(test.data[colKey])) {
            if (!subtableColumnsKeys.includes(colKey)) {
              subtableColumnsKeys.push(colKey);
            }
          }
          // Optimization: if we found it's a subtable, no need to check other tests for this colKey
          // However, we need to check all tests to collect all possible subtable headers later.
        }
      }
    });

    const regularColumns = columnsForExport.filter(col => !subtableColumnsKeys.includes(col));
    const expandedSubtableColumnHeaders = new Map<string, string[]>(); // Map: originalSubtableKey -> its headers

    subtableColumnsKeys.forEach(colKey => {
      const allSubHeaders = new Set<string>();
      tests.forEach(test => {
        if (test.data && this.isSubTable(test.data[colKey])) {
          const subHeaders = this.getSubtableHeaders(test.data[colKey]);
          subHeaders.forEach(header => allSubHeaders.add(header));
        }
      });
      expandedSubtableColumnHeaders.set(colKey, Array.from(allSubHeaders));
    });

    // Determine the final Excel column headers (regular + expanded subtable headers)
    const finalExcelColumnHeaders: string[] = [...regularColumns];
    subtableColumnsKeys.forEach(originalSubKey => {
      const headers = expandedSubtableColumnHeaders.get(originalSubKey) || [];
      headers.forEach(header => {
        finalExcelColumnHeaders.push(`${originalSubKey} - ${header}`);
      });
    });

    // Ensure 'ID' is first if it's selected
    const idIndex = finalExcelColumnHeaders.indexOf('ID');
    if (idIndex > 0) {
      const idCol = finalExcelColumnHeaders.splice(idIndex, 1)[0];
      finalExcelColumnHeaders.unshift(idCol);
    }

    // --- Start ExcelJS Workbook creation ---
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(excelOptions.sheetName);

    // Set up columns with minimum widths (headers only at this stage)
    worksheet.columns = finalExcelColumnHeaders.map(header => ({
      header: header, // This sets the header text in ExcelJS
      key: header,    // Key for mapping data later if needed, though we add by array
      width: Math.max(15, Math.min(35, header.length * 1.2 + 5)) // Initial width
    }));

    // Apply header row style (ExcelJS adds header row automatically based on worksheet.columns)
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: '000000' } };
    headerRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    headerRow.height = 30;
    const borderColor = 'D9D9D9';
    const primaryGreen = '92D050'; // For subtable headers

    headerRow.eachCell((cell, colNumber) => {
      const headerText = finalExcelColumnHeaders[colNumber - 1];
      if (headerText && headerText.includes(' - ')) { // Is a subtable expanded header
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: primaryGreen } };
      } else {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F2F2F2' } };
      }
      cell.border = {
        top: { style: 'thin', color: { argb: borderColor } }, left: { style: 'thin', color: { argb: borderColor } },
        bottom: { style: 'thin', color: { argb: borderColor } }, right: { style: 'thin', color: { argb: borderColor } }
      };
    });
    // --- End Header Setup ---

    let testCounter = 0;
    const allRowsForExcel: any[][] = []; // Array of arrays for addRows

    tests.forEach(test => {
      testCounter++;
      const baseRowDataMap = new Map<string, any>();

      // Populate regular columns, handling 'ID' specifically
      regularColumns.forEach(colKey => {
        if (colKey === 'ID') {
          baseRowDataMap.set(colKey, this.formatCellValueForExcel(test.metadata.sequentialId !== undefined ? test.metadata.sequentialId : (test.metadata.testId || '')));
        } else if (test.data) {
          baseRowDataMap.set(colKey, this.formatCellValueForExcel(test.data[colKey]));
        } else {
          baseRowDataMap.set(colKey, '');
        }
      });

      const rowsForCurrentTest: any[][] = [];
      let hasSubtablesToExpandForThisTest = subtableColumnsKeys.some(colKey => test.data && this.isSubTable(test.data[colKey]));

      if (!hasSubtablesToExpandForThisTest) {
        const singleRowValues: any[] = [];
        finalExcelColumnHeaders.forEach(header => {
          singleRowValues.push(baseRowDataMap.get(header) || ''); // Get from baseRowDataMap or empty
        });
        rowsForCurrentTest.push([...singleRowValues, testCounter]); // Add __testGroup at the end
      } else {
        let maxSubRowsForThisTest = 1;
        subtableColumnsKeys.forEach(colKey => {
          if (test.data && this.isSubTable(test.data[colKey])) {
            maxSubRowsForThisTest = Math.max(maxSubRowsForThisTest, this.getSubTableRowCount(test.data[colKey]));
          }
        });
        maxSubRowsForThisTest = Math.max(1, maxSubRowsForThisTest);

        for (let i = 0; i < maxSubRowsForThisTest; i++) {
          const currentSubRowMap = new Map<string, any>(baseRowDataMap); // Start with base data

          subtableColumnsKeys.forEach(originalSubKey => {
            const allPossibleSubHeaders = expandedSubtableColumnHeaders.get(originalSubKey) || [];
            if (test.data && this.isSubTable(test.data[originalSubKey])) {
              const subRowsFromData = this.getSubTableRows(test.data[originalSubKey]);
              const subRowDataForCurrentIndex = subRowsFromData[i] || {};

              allPossibleSubHeaders.forEach(subHeader => {
                const fullSubColKey = `${originalSubKey} - ${subHeader}`;
                currentSubRowMap.set(fullSubColKey, this.formatCellValueForExcel(subRowDataForCurrentIndex[subHeader]));
              });
            } else { // If this test doesn't have this subtable, or subtable is empty
              allPossibleSubHeaders.forEach(subHeader => {
                const fullSubColKey = `${originalSubKey} - ${subHeader}`;
                currentSubRowMap.set(fullSubColKey, ''); // Fill with empty for its expanded headers
              });
            }
          });
          const subRowValues: any[] = [];
          finalExcelColumnHeaders.forEach(header => {
            subRowValues.push(currentSubRowMap.get(header) || '');
          });
          rowsForCurrentTest.push([...subRowValues, testCounter]); // Add __testGroup at the end
        }
      }

      // Apply optimization logic from optimizeSubtableRows here for rowsForCurrentTest
      const optimizedRowsForCurrentTest = this.optimizeRowsForSingleTest(rowsForCurrentTest, finalExcelColumnHeaders, subtableColumnsKeys.map(sk => expandedSubtableColumnHeaders.get(sk) || []).flat().map(h => ` ${h}`));
      allRowsForExcel.push(...optimizedRowsForCurrentTest);
    });

    // Add all processed rows to the worksheet.
    // The last element of each inner array is the '__testGroup' number.
    // We pass arrays of values, not objects, to addRows for efficiency.
    worksheet.addRows(allRowsForExcel.map(rowWithGroup => rowWithGroup.slice(0, -1))); // Exclude the __testGroup

    // --- Apply Styles and Final Touches (Pass allRowsForExcel for __testGroup info) ---
    this.applyStylingToWorksheet(worksheet, finalExcelColumnHeaders, allRowsForExcel, excelOptions);

    // Generate Excel file
    try {
      const buffer = await workbook.xlsx.writeBuffer(); // Use await
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${filename}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error writing Excel buffer or creating blob:', error);
    }
  }

  // New helper to optimize rows for a single test before adding to main list
  private optimizeRowsForSingleTest(rows: any[][], finalColumnHeaders: string[], subtableExpandedHeaderFragments: string[]): any[][] {
    if (!rows || rows.length === 0) return [];
    if (rows.length === 1) return rows; // No optimization if only one row (base or single sub-row)

    const subtableColumnIndices = finalColumnHeaders.map((header, index) => {
      // Check if header corresponds to an expanded subtable column
      return subtableExpandedHeaderFragments.some(frag => header.includes(frag)) ? index : -1;
    }).filter(index => index !== -1);

    if (subtableColumnIndices.length === 0) { // No actual subtable columns found in final headers
      return [rows[0]]; // Keep only the first (base) row
    }

    const rowsWithSubtableContent: any[][] = [];
    rows.forEach(rowDataArray => {
      // Check if any of the subtable columns in this rowDataArray have content
      const hasFilledSubtable = subtableColumnIndices.some(subColIndex => {
        const cellValue = rowDataArray[subColIndex];
        return cellValue !== null && cellValue !== undefined && cellValue !== '';
      });
      if (hasFilledSubtable) {
        rowsWithSubtableContent.push(rowDataArray);
      }
    });

    if (rowsWithSubtableContent.length > 0) {
      return rowsWithSubtableContent;
    } else {
      return [rows[0]]; // If no subtable rows had content, return the first (base) row
    }
  }

  private applyStylingToWorksheet(
    worksheet: ExcelJS.Worksheet,
    finalExcelColumnHeaders: string[],
    dataWithTestGroup: any[][], // Rows now include __testGroup as the last element
    options: { addFilters?: boolean; freezeHeader?: boolean }
  ): void {
    const borderColor = 'D9D9D9';
    const primaryGreen = '92D050';
    const secondaryGreen = '70AD47';
    const alternateRowColor = 'F5F5F5'; // A light grey
    const separatorLineColor = 'AAAAAA';

    const subtableColumnIndices = finalExcelColumnHeaders
      .map((header, index) => header.includes(' - ') ? index + 1 : -1) // 1-based index for ExcelJS
      .filter(index => index !== -1);

    // Process rows for styling (dataWithTestGroup includes __testGroup at the end)
    // Row indexing for worksheet.getRow() is 1-based. Header is row 1. Data starts at row 2.
    let currentPhysicalRowIndex = 2; // Start from the first data row
    const testGroupBoundaries: { startRow: number, endRow: number, isAlternate: boolean }[] = [];
    if (dataWithTestGroup.length > 0) {
      let currentTestGroupVal = dataWithTestGroup[0][dataWithTestGroup[0].length - 1];
      let groupStartPhysicalRow = 2;
      let groupIsAlternate = false;

      for (let i = 0; i < dataWithTestGroup.length; i++) {
        const rowData = dataWithTestGroup[i];
        const testGroupVal = rowData[rowData.length - 1];

        if (testGroupVal !== currentTestGroupVal) {
          testGroupBoundaries.push({ startRow: groupStartPhysicalRow, endRow: currentPhysicalRowIndex - 1, isAlternate: groupIsAlternate });
          groupStartPhysicalRow = currentPhysicalRowIndex;
          currentTestGroupVal = testGroupVal;
          groupIsAlternate = !groupIsAlternate;
        }
        currentPhysicalRowIndex++;
      }
      // Add the last group
      testGroupBoundaries.push({ startRow: groupStartPhysicalRow, endRow: currentPhysicalRowIndex - 1, isAlternate: groupIsAlternate });
    }


    testGroupBoundaries.forEach(group => {
      for (let rowIndex = group.startRow; rowIndex <= group.endRow; rowIndex++) {
        const row = worksheet.getRow(rowIndex);
        if (!row) continue;

        let maxLines = 1;
        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          if (cell && cell.value != null) {
            const cellValue = String(cell.value);
            if (cellValue.includes('\n')) {
              maxLines = Math.max(maxLines, cellValue.split('\n').length);
            } else {
              const colWidth = worksheet.getColumn(colNumber).width || 12;
              const charsPerLine = Math.max(5, colWidth / 0.9); // Avoid division by zero or too small width
              if (cellValue.length > charsPerLine) {
                maxLines = Math.max(maxLines, Math.min(5, Math.ceil(cellValue.length / charsPerLine)));
              }
            }
          }
        });
        row.height = Math.min(120, Math.max(20, 18 + (maxLines - 1) * 15));


        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          cell.alignment = { vertical: 'middle', wrapText: true, indent: cell.value && String(cell.value).startsWith('•') ? 1 : 0 };
          cell.border = {
            top: { style: 'thin', color: { argb: borderColor } }, left: { style: 'thin', color: { argb: borderColor } },
            bottom: { style: 'thin', color: { argb: borderColor } }, right: { style: 'thin', color: { argb: borderColor } }
          };

          if (subtableColumnIndices.includes(colNumber)) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: group.isAlternate ? secondaryGreen : primaryGreen } };
          } else if (group.isAlternate) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: alternateRowColor } };
          } else {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } }; // White for non-alternate main columns
          }
        });
      }
      // Add separator line
      if (group.endRow < worksheet.rowCount) { // worksheet.rowCount is based on actual data rows
        const lastRowOfGroup = worksheet.getRow(group.endRow);
        lastRowOfGroup.eachCell({ includeEmpty: true }, (cell) => {
          cell.border = { ...cell.border, bottom: { style: 'medium', color: { argb: separatorLineColor } } };
        });
      }
    });

    // Adjust column widths based on content dynamically
    finalExcelColumnHeaders.forEach((header, i) => {
      let maxLength = header.length; // Start with header length
      let hasBullets = false;
      for (let r = 2; r <= worksheet.rowCount; r++) { // Iterate through actual data rows
        const cell = worksheet.getCell(r, i + 1);
        if (cell && cell.value) {
          const valueStr = String(cell.value);
          if (valueStr.includes('•')) hasBullets = true;
          // For multiline cells, consider the longest line
          const lines = valueStr.split('\n');
          const longestLineLength = Math.max(...lines.map(line => line.length));
          maxLength = Math.max(maxLength, longestLineLength);
        }
      }
      let newWidth = Math.min(60, Math.max(12, maxLength * (hasBullets ? 1.1 : 0.9) + (hasBullets ? 3 : 0)));
      if (header === 'ID') newWidth = Math.max(8, Math.min(12, newWidth)); // ID column specific width
      worksheet.getColumn(i + 1).width = newWidth;
    });


    if (options.addFilters && worksheet.rowCount > 0) {
      worksheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: finalExcelColumnHeaders.length }
      };
    }

    if (options.freezeHeader && worksheet.rowCount > 0) {
      worksheet.views = [{ state: 'frozen', ySplit: 1 }];
    }
  }

  private getSubtableHeaders(subtable: any): string[] {
    if (!subtable) return [];
    if (subtable.headers && Array.isArray(subtable.headers)) {
      return subtable.headers;
    } else if (Array.isArray(subtable) && subtable.length > 0) {
      const allKeys = new Set<string>();
      subtable.forEach(row => {
        if (row && typeof row === 'object') {
          Object.keys(row).forEach(key => allKeys.add(key));
        }
      });
      return Array.from(allKeys);
    }
    return [];
  }

  private formatCellValueForExcel(value: any): any {
    if (value === null || value === undefined) {
      return '';
    }
    if (Array.isArray(value)) {
      return value.map(item => `• ${StripHtmlPipe.strip(String(item))}`).join('\n');
    }
    if (typeof value === 'string') {
      // Strip HTML tags before Excel formatting
      const cleaned = StripHtmlPipe.strip(value);
      if (cleaned.includes(';')) {
        return cleaned.split(';').map(item => item.trim()).filter(item => item.length > 0).map(item => `• ${item}`).join('\n');
      }
      return cleaned;
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch (e) {
        return '[Complex Object]';
      }
    }
    return value;
  }

  private getSubTableRows(subTable: any): any[] {
    if (!subTable) return [];
    this.ensureMemoizationCache(); // Ensure cache is initialized
    const cacheKey = JSON.stringify(subTable);
    if (this.memoizedSubTableRows && this.memoizedSubTableRows.has(cacheKey)) {
      return this.memoizedSubTableRows.get(cacheKey)!;
    }
    let rows: any[] = [];
    if (subTable.headers && Array.isArray(subTable.rows)) {
      rows = subTable.rows.map((row: any[]) => {
        return subTable.headers.reduce((obj: any, header: string, index: number) => {
          obj[header] = row[index];
          return obj;
        }, {});
      });
    } else if (Array.isArray(subTable)) {
      rows = subTable;
    } else if (subTable.rows && Array.isArray(subTable.rows)) {
      rows = subTable.rows;
    }
    if (this.memoizedSubTableRows) {
      this.memoizedSubTableRows.set(cacheKey, rows);
    }
    return rows;
  }

  private ensureMemoizationCache(): void {
    if (!this.memoizedSubTableRows) {
      this.memoizedSubTableRows = new Map<string, any[]>();
    }
  }
}
